// The companion speed dial of an open app: ONE component for cortex's Apps page (apps/web wires it to its
// api and pages) and a project's standalone app (this shell), a one-to-one port of the old cortex dial
// (AssistantChat.jsx `speedDial`): one 44px round #3c5d88 button at bottom-6/right-6, 55% opaque until
// hovered or open, a plus that turns into an X; the companions fan out ABOVE it as coloured pills, chat
// nearest the button. WHICH companions is the app's declaration (GET …/apps/:app/companions, the
// structure routes), as the host reads it. A page companion opens over the app in a large window, so the
// app stays where it was; the chat and the guides open their own overlays. The dial holds only the
// companions; there is no bar.
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@cortex/ui';
import {
  BookOpen,
  Brain,
  ExternalLink,
  GitBranch,
  GraduationCap,
  MessageSquare,
  Plus,
  ShieldCheck,
  Workflow,
  X,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router';
import { OPEN_CHAT_EVENT, START_TUTORIAL_EVENT } from './companions.tsx';

export type Companion = { key: string; label: string; page: string | null };

/** What the dial reads from its host: the companions, their pages and where a page opens full. */
export interface DialHost {
  companions(slug: string, branch: string, app: string): Promise<Companion[]>;
  pages: Record<string, () => Promise<React.ComponentType>>;
  pagePath(page: string, slug: string, branch: string): string;
}

/** Per companion: its icon and colour — the old in-cortex dial's (AssistantChat.jsx DIAL_ITEMS). */
const CHROME: Record<string, { icon: LucideIcon; bg: string }> = {
  chat: { icon: MessageSquare, bg: '#3c5d88' },
  workflows: { icon: Workflow, bg: '#0f172a' },
  guides: { icon: GraduationCap, bg: '#7c3aed' },
  models: { icon: Brain, bg: '#0e7490' },
  pipeline: { icon: GitBranch, bg: '#4f46e5' },
  governance: { icon: ShieldCheck, bg: '#059669' },
  docs: { icon: BookOpen, bg: '#b45309' },
};

const DIAL_SHADOW = '0 4px 14px rgba(0,0,0,.28)';
/**
 * A pill's edge: the old shadow plus a 1px light ring. The old dial had only the shadow (AssistantChat.jsx
 * `boxShadow: "0 4px 14px rgba(0,0,0,.28)"`), and a dark shadow does not show on a dark app, so the
 * Workflows pill (#0f172a) disappeared against dark themes. The ring is white at 28% opacity, which is
 * invisible on a light app, so light themes look exactly like the old dial. On a dark app it outlines
 * every pill.
 */
const PILL_SHADOW = `0 0 0 1px rgba(255,255,255,.28), ${DIAL_SHADOW}`;

const lazyPages = new Map<string, React.LazyExoticComponent<React.ComponentType>>();
function pageComponent(pages: Record<string, () => Promise<React.ComponentType>>, page: string) {
  const load = pages[page];
  if (!load) return null;
  let c = lazyPages.get(page);
  if (!c) lazyPages.set(page, (c = React.lazy(() => load().then((Page) => ({ default: Page })))));
  return c;
}

function CompanionWindow({
  host,
  companion,
  slug,
  branch,
  onClose,
}: {
  host: DialHost;
  companion: Companion & { page: string };
  slug: string;
  branch: string;
  onClose: () => void;
}) {
  const Page = pageComponent(host.pages, companion.page);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="companion-window"
        data-companion={companion.key}
        className="bg-page-bg border-0 rounded-[10px] p-0 gap-0 shadow-2xl text-page-text sm:max-w-[min(1200px,94vw)] w-[94vw] h-[88vh] flex flex-col overflow-hidden"
      >
        <DialogHeader className="flex flex-row items-center gap-2 px-4 h-12 shrink-0 border-b border-page-divider bg-card-bg text-left space-y-0">
          <DialogTitle className="text-base font-semibold flex-1">{companion.label}</DialogTitle>
          <DialogDescription className="sr-only">
            The {companion.label} page of this project
          </DialogDescription>
          <Link
            to={host.pagePath(companion.page, slug, branch)}
            onClick={onClose}
            data-testid="companion-open-page"
            className="inline-flex items-center gap-1 text-xs text-nav-active no-underline hover:underline mr-8"
          >
            <ExternalLink size={13} /> Open the full page
          </Link>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto" data-testid="companion-body">
          {Page ? (
            <React.Suspense fallback={<div className="p-6 text-sm text-page-muted">Loading…</div>}>
              <Page />
            </React.Suspense>
          ) : (
            <div className="p-6 text-sm text-page-muted">This page is not available.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CompanionDial({
  host,
  slug,
  branch,
  app,
  hidden = false,
}: {
  host: DialHost;
  slug: string;
  branch: string;
  app: string;
  /** The chat window is open: the dial steps aside (the old launcher did the same). */
  hidden?: boolean;
}) {
  const [companions, setCompanions] = React.useState<Companion[] | null>(null);
  const [open, setOpen] = React.useState(false);
  const [showing, setShowing] = React.useState<(Companion & { page: string }) | null>(null);

  React.useEffect(() => {
    let alive = true;
    setCompanions(null);
    setOpen(false);
    host
      .companions(slug, branch, app)
      .then((c) => alive && setCompanions(c))
      .catch(() => alive && setCompanions([]));
    return () => {
      alive = false;
    };
    // The host's reader is stable (a module constant or a memo); the app is what changes.
  }, [slug, branch, app]);

  React.useEffect(() => {
    if (hidden) setOpen(false);
  }, [hidden]);

  const pick = (c: Companion) => {
    setOpen(false);
    if (c.key === 'chat') window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT, { detail: {} }));
    else if (c.key === 'guides') window.dispatchEvent(new CustomEvent(START_TUTORIAL_EVENT));
    else if (c.page) setShowing({ ...c, page: c.page });
  };

  return (
    <>
      {!hidden && (
        <div
          className="fixed bottom-6 right-6 z-[9999] hidden md:flex flex-col-reverse items-end gap-2"
          data-testid="companion-dial"
          data-companions={(companions ?? []).map((c) => c.key).join(',')}
        >
          <button
            type="button"
            data-testid="app-speed-dial"
            aria-expanded={open}
            aria-label={open ? 'Close' : 'Companions'}
            title={open ? 'Close' : 'Companions'}
            onClick={() => setOpen((v) => !v)}
            style={{ background: '#3c5d88', boxShadow: DIAL_SHADOW, transition: 'opacity .2s' }}
            className={
              'flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 text-white hover:opacity-100 ' +
              (open ? 'opacity-100' : 'opacity-[.55]')
            }
          >
            {open ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          </button>
          {open &&
            (companions ?? []).map((c) => {
              const ch = CHROME[c.key];
              if (!ch) return null;
              const Icon = ch.icon;
              return (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`dial-${c.key}`}
                  onClick={() => pick(c)}
                  style={{ background: ch.bg, boxShadow: PILL_SHADOW }}
                  className="flex h-10 cursor-pointer items-center gap-2 rounded-full border-0 pl-3 pr-4 text-sm font-medium text-white transition-all hover:opacity-90"
                >
                  <Icon className="h-5 w-5" /> {c.label}
                </button>
              );
            })}
        </div>
      )}
      {showing && (
        <CompanionWindow
          host={host}
          companion={showing}
          slug={slug}
          branch={branch}
          onClose={() => setShowing(null)}
        />
      )}
    </>
  );
}
